import CssBaseline from "@mui/material/CssBaseline";
import ScopedCssBaseline from "@mui/material/ScopedCssBaseline";
import StyledEngineProvider from "@mui/material/StyledEngineProvider";
import type { CssVarsThemeOptions } from "@mui/material/styles";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import { deepMerge } from "@resamare/functions";
import { PropsWithChildren, ReactElement, useEffect } from "react";
import styles from "./Baseline.module.scss";
import { defaultCustomTheme } from "./config/theme";
import { FONT_FAMILY } from "./config/theme.constants";
import { generateTheme } from "./utils";

export type ThemeOptions = CssVarsThemeOptions;

export type ThemeProviderProps = PropsWithChildren<{
    theme?: ThemeOptions;
    scoped?: boolean;
    scopedContainer?: boolean;
}>;

export function ThemeProvider({
    children,
    theme = {},
    scoped = false,
    scopedContainer = false,
}: ThemeProviderProps): ReactElement {
    const customTheme = generateTheme(deepMerge(defaultCustomTheme, theme));

    useEffect(() => {
        document.documentElement.style.setProperty(`--resamare-spacing`, customTheme.spacing(1));
        document.documentElement.style.setProperty(
            `--resamare-font-family`,
            customTheme.typography.fontFamily || FONT_FAMILY.MAIN,
        );
    }, [customTheme]);

    return (
        <CssVarsProvider theme={customTheme}>
            <StyledEngineProvider injectFirst>
                {scoped ? (
                    <ScopedCssBaseline>
                        <div
                            className={`${styles["baseline"]} ${
                                scopedContainer ? styles["baseline-container"] : ""
                            }`}
                        >
                            {children}
                        </div>
                    </ScopedCssBaseline>
                ) : (
                    <CssBaseline>{children}</CssBaseline>
                )}
            </StyledEngineProvider>
        </CssVarsProvider>
    );
}
